// API client. Every call goes to the *currently active* backend URL.
// Tab switch = swap `activeBackendUrl` (held in App state).

import type {
  AccountView,
  ActivityEntry,
  ActivityRollupResponse,
  AgentHistory,
  AgentView,
  BacklogTaskView,
  BackendsResponse,
  ChatMessage,
  FleetGitHealthResponse,
  FromRole,
  LoginResponse,
  ModeInfo,
  ReassignRequest,
  StateResponse,
  UsageReport,
} from "./types";

class HttpError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public body: string,
  ) {
    super(`${status} ${statusText}: ${body}`);
    this.name = "HttpError";
  }
}

// Centralized model (2026-05-22): the dashboard talks to ONE backend — the
// central API. Per-VM interaction goes through that API's /api/vms/<id>/* proxy
// (the baseUrl carries the VM segment), so there is exactly ONE token: minted by
// logging into the central API, valid on every call. (The fleet shares one JWT
// secret from GCS, so even proxied calls validate.) The old per-backend token
// map is gone — there are no longer multiple direct backends to disambiguate.
let authToken: string | null = null;

export function setAuthToken(token: string | null): void {
  authToken = token;
}

export function clearAuthToken(): void {
  authToken = null;
}

async function http<T>(baseUrl: string, path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init?.headers as Record<string, string> | undefined),
  };
  if (authToken) {
    headers["Authorization"] = `Bearer ${authToken}`;
  }
  const res = await fetch(`${baseUrl}${path}`, { ...init, headers });
  if (!res.ok) {
    const body = await res.text();
    throw new HttpError(res.status, res.statusText, body);
  }
  return res.json() as Promise<T>;
}

export { HttpError };

export const api = {
  state: (baseUrl: string) => http<StateResponse>(baseUrl, "/api/state"),
  fleetGitHealth: (baseUrl: string, scope: "local" | "fleet" = "fleet") =>
    http<FleetGitHealthResponse>(baseUrl, `/api/fleet/git-health?scope=${scope}`),
  // Activity feed (Plan B): filters applied in SQL BEFORE the limit + `before_id`
  // cursor for "Load older". `types` is a comma-separated event-type set (the
  // category tabs pass their ALERT/OPS sets); `slot`/`type` are single-value filters.
  activity: (
    baseUrl: string,
    opts: { limit?: number; slot?: number; type?: string; types?: string; before_id?: number } = {},
  ) => {
    const q = new URLSearchParams({ limit: String(opts.limit ?? 50) });
    if (opts.slot != null) q.set("slot", String(opts.slot));
    if (opts.type) q.set("type", opts.type);
    if (opts.types) q.set("types", opts.types);
    if (opts.before_id != null) q.set("before_id", String(opts.before_id));
    return http<ActivityEntry[]>(baseUrl, `/api/activity?${q}`);
  },
  // Denoise rollup — event_type counts within a window → the "×N in last 1h" badges.
  activityRollup: (baseUrl: string, opts: { within_minutes?: number; by_slot?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (opts.within_minutes != null) q.set("within_minutes", String(opts.within_minutes));
    if (opts.by_slot) q.set("by_slot", "true");
    const qs = q.toString();
    return http<ActivityRollupResponse>(baseUrl, `/api/activity/rollup${qs ? `?${qs}` : ""}`);
  },
  backlog: (baseUrl: string, status?: string) =>
    http<BacklogTaskView[]>(baseUrl, status ? `/api/backlog?status=${status}` : "/api/backlog"),
  taskBlockers: (baseUrl: string, taskId: string) =>
    http<{ task_id: string; explanation: string }>(baseUrl, `/api/backlog/${encodeURIComponent(taskId)}/blockers`),
  reloadBacklog: (baseUrl: string) =>
    http<{ ok: boolean; loaded?: number }>(baseUrl, "/api/backlog/reload", {
      method: "POST",
    }),

  reloadPrompts: (baseUrl: string) =>
    http<{ ok: boolean; loaded: number; roles: string[] }>(baseUrl, "/api/prompts/reload", {
      method: "POST",
    }),

  spawnPreview: (
    baseUrl: string,
    params: {
      slot_id: number;
      server_url: string;
      account_id?: string;
      operator?: string;
      model?: string;
      effort?: string;
      thinking?: string;
    },
  ) => {
    const q = new URLSearchParams({
      slot_id: String(params.slot_id),
      server_url: params.server_url,
      ...(params.account_id ? { account_id: params.account_id } : {}),
      ...(params.operator ? { operator: params.operator } : {}),
      ...(params.model ? { model: params.model } : {}),
      ...(params.effort ? { effort: params.effort } : {}),
      ...(params.thinking ? { thinking: params.thinking } : {}),
    });
    return http<{ role: string; prompt: string }>(baseUrl, `/api/spawn/preview?${q}`);
  },

  spawnAgentPreview: (
    baseUrl: string,
    params: {
      role: string;
      server_url: string;
      machine?: string;
      rc_url?: string;
      model?: string;
      effort?: string;
      thinking?: string;
    },
  ) => {
    const q = new URLSearchParams({
      role: params.role,
      server_url: params.server_url,
      ...(params.machine ? { machine: params.machine } : {}),
      ...(params.rc_url ? { rc_url: params.rc_url } : {}),
      ...(params.model ? { model: params.model } : {}),
      ...(params.effort ? { effort: params.effort } : {}),
      ...(params.thinking ? { thinking: params.thinking } : {}),
    });
    return http<{ role: string; prompt: string }>(baseUrl, `/api/spawn/agent-preview?${q}`);
  },

  spawnSlot: (baseUrl: string, slotId: number, body: { account_id?: string; boot_prompt: string; cwd?: string }) =>
    http<{ ok: boolean; slot_id: number; tmux_session: string; attach_hint: string }>(
      baseUrl,
      `/api/slots/${slotId}/spawn`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  spawnAgent: (
    baseUrl: string,
    body: {
      role: string;
      label: string;
      // Required (Phase 4b-cleanup): server hard-fails with 400 if missing.
      // The named account's oauth_token_env_file is sourced into the spawned
      // claude session so the worker authenticates via setup-token, not the
      // ambient ~/.claude/.credentials.json.
      account_id: string;
      machine?: string;
      model?: string;
      effort?: string;
      thinking?: boolean;
      poll_interval_seconds?: number;
      boot_prompt: string;
    },
  ) =>
    http<{
      ok: boolean;
      agent_id: string;
      tmux_session: string;
      attach_hint: string;
      rc_url: string | null;
      rc_url_warning: string | null;
    }>(baseUrl, `/api/agents/spawn`, { method: "POST", body: JSON.stringify(body) }),

  // Drives `claude /usage` on the backend's machine and updates the account.
  // Slow (~10-15s) — show a spinner. Returns the fresh AccountView.
  refreshAccountUsage: (baseUrl: string, accountId: string) =>
    http<AccountView>(baseUrl, `/api/accounts/${encodeURIComponent(accountId)}/refresh-usage`, {
      method: "POST",
    }),
  backends: (baseUrl: string) => http<BackendsResponse>(baseUrl, "/api/backends"),
  healthz: (baseUrl: string) => http<{ status: string; ts: string }>(baseUrl, "/api/healthz"),

  setPrerequisite: (baseUrl: string, name: string, value: boolean, setBy = "operator") =>
    http<unknown>(baseUrl, `/api/prerequisites/${encodeURIComponent(name)}`, {
      method: "POST",
      body: JSON.stringify({ value, set_by: setBy }),
    }),

  answerBlocked: (baseUrl: string, blockedId: string, answer: string, fromRole: FromRole) =>
    http<unknown>(baseUrl, `/api/blocked/${encodeURIComponent(blockedId)}/answer`, {
      method: "POST",
      body: JSON.stringify({ answer, from_role: fromRole }),
    }),

  sendMessage: (baseUrl: string, slotId: number, text: string, fromRole: FromRole) =>
    http<unknown>(baseUrl, `/api/slots/${slotId}/message`, {
      method: "POST",
      body: JSON.stringify({ text, from_role: fromRole }),
    }),

  reassign: (baseUrl: string, slotId: number, body?: ReassignRequest) =>
    http<unknown>(baseUrl, `/api/slots/${slotId}/reassign`, {
      method: "POST",
      ...(body ? { body: JSON.stringify(body) } : {}),
    }),

  deleteSlot: (baseUrl: string, slotId: number) =>
    http<{ ok: boolean; slot_id: number; killed_tmux: string | null }>(baseUrl, `/api/slots/${slotId}`, {
      method: "DELETE",
    }),

  slotLog: (baseUrl: string, slotId: number, historyLines = 200) =>
    http<{ slot_id: number; tmux_session: string | null; lines: string; available: boolean }>(
      baseUrl,
      `/api/slots/${slotId}/log?history_lines=${historyLines}`,
    ),

  agentLog: (baseUrl: string, agentId: string, historyLines = 200) =>
    http<{ agent_id: string; tmux_session: string | null; lines: string; available: boolean }>(
      baseUrl,
      `/api/agents/${encodeURIComponent(agentId)}/log?history_lines=${historyLines}`,
    ),

  pause: (baseUrl: string, slotId: number) => http<unknown>(baseUrl, `/api/slots/${slotId}/pause`, { method: "POST" }),

  resume: (baseUrl: string, slotId: number) =>
    http<unknown>(baseUrl, `/api/slots/${slotId}/resume`, { method: "POST" }),

  // Re-issue a live agent's /loop cadence (nudge, no respawn). seconds is bounded
  // server-side to [30, 3600]; the dashboard pre-clamps via clampLoopSeconds().
  setLoopInterval: (baseUrl: string, slotId: number, seconds: number) =>
    http<{ slot_id: number; seconds: number; delivered: boolean }>(baseUrl, `/api/slots/${slotId}/loop-interval`, {
      method: "POST",
      body: JSON.stringify({ seconds }),
    }),

  snapshot: (baseUrl: string, reason = "manual") =>
    http<unknown>(baseUrl, `/api/snapshot?reason=${encodeURIComponent(reason)}`, {
      method: "POST",
    }),

  login: (baseUrl: string, username: string, password: string, backendId?: string) =>
    http<LoginResponse>(baseUrl, "/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password, backend_id: backendId }),
    }),

  logout: (baseUrl: string) => http<unknown>(baseUrl, "/api/auth/logout", { method: "POST" }),

  listAccounts: (baseUrl: string) => http<AccountView[]>(baseUrl, "/api/accounts"),

  reportUsage: (baseUrl: string, accountId: string, report: UsageReport) =>
    http<AccountView>(baseUrl, `/api/accounts/${encodeURIComponent(accountId)}/usage`, {
      method: "POST",
      body: JSON.stringify(report),
    }),

  markRateLimited: (baseUrl: string, accountId: string, rateLimitedUntil: string, note?: string) =>
    http<AccountView>(baseUrl, `/api/accounts/${encodeURIComponent(accountId)}/rate-limited`, {
      method: "POST",
      body: JSON.stringify({ rate_limited_until: rateLimitedUntil, note }),
    }),

  clearRateLimit: (baseUrl: string, accountId: string) =>
    http<AccountView>(baseUrl, `/api/accounts/${encodeURIComponent(accountId)}/clear-rate-limit`, {
      method: "POST",
    }),

  mode: (baseUrl: string) => http<ModeInfo>(baseUrl, "/api/mode"),

  // List agents (Plan B): filterable. Default EXCLUDES terminal (archived/finished)
  // so the live roster stays clean; pass include_finished=true (or status) to see PAST runs.
  // kind/lifecycle narrow by the two-axis classification; limit caps the result.
  listAgents: (
    baseUrl: string,
    opts: {
      status?: string;
      kind?: string;
      lifecycle?: string;
      include_finished?: boolean;
      limit?: number;
    } = {},
  ) => {
    const q = new URLSearchParams();
    if (opts.status) q.set("status", opts.status);
    if (opts.kind) q.set("kind", opts.kind);
    if (opts.lifecycle) q.set("lifecycle", opts.lifecycle);
    if (opts.include_finished) q.set("include_finished", "true");
    if (opts.limit != null) q.set("limit", String(opts.limit));
    const qs = q.toString();
    return http<AgentView[]>(baseUrl, `/api/agents${qs ? `?${qs}` : ""}`);
  },

  agentHistoryByRole: (baseUrl: string, role: string, limit = 200) =>
    http<AgentHistory>(baseUrl, `/api/agents/by-role/${encodeURIComponent(role)}/history?limit=${limit}`),

  sendToRole: (baseUrl: string, role: string, text: string, fromRole: FromRole = "operator") =>
    http<ChatMessage>(baseUrl, `/api/agents/by-role/${encodeURIComponent(role)}/message`, {
      method: "POST",
      body: JSON.stringify({ text, from_role: fromRole }),
    }),

  promoteAgent: (baseUrl: string, agentId: string, targetRole: string) =>
    http<AgentView>(baseUrl, `/api/agents/${encodeURIComponent(agentId)}/promote`, {
      method: "POST",
      body: JSON.stringify({ target_role: targetRole }),
    }),

  archiveAgent: (baseUrl: string, agentId: string) =>
    http<AgentView>(baseUrl, `/api/agents/${encodeURIComponent(agentId)}/archive`, { method: "POST" }),

  restoreAgent: (baseUrl: string, agentId: string) =>
    http<AgentView>(baseUrl, `/api/agents/${encodeURIComponent(agentId)}/restore`, { method: "POST" }),

  deleteAgent: (baseUrl: string, agentId: string) =>
    http<{ ok: boolean; agent_id: string }>(baseUrl, `/api/agents/${encodeURIComponent(agentId)}`, {
      method: "DELETE",
    }),

  updateAgent: (
    baseUrl: string,
    agentId: string,
    fields: { label?: string; rc_url?: string; machine?: string; model?: string },
  ) =>
    http<AgentView>(baseUrl, `/api/agents/${encodeURIComponent(agentId)}/update`, {
      method: "POST",
      body: JSON.stringify(fields),
    }),

  adminPopulate: (baseUrl: string) =>
    http<{ ok: boolean; stdout: string[]; stderr: string[] }>(baseUrl, "/api/admin/populate", { method: "POST" }),

  adminReset: (baseUrl: string) =>
    http<{ ok: boolean; db_path: string; tasks_synced: number; accounts_synced: number }>(baseUrl, "/api/admin/reset", {
      method: "POST",
    }),
};
